import React, { MouseEvent, PropsWithChildren } from 'react';
import styled from 'styled-components';

import { ModalClose, ModalContainer } from './CommonModal';
import ActionButton from '../buttons/ActionButton';
import { useUserContext } from '../../contexts/UserContext';

import { BLACK } from 'Common/src/lib/Colors';
import { ULogApplication, ULogSeverity, ULogTag, logSumoEvent } from 'Common/src/api/SumoLogicApi';
import CloseIcon from 'Common/src/components/icons/CloseIcon';

interface Props {
  title?: string;
  subtitle?: string;
  secondaryButtonLabel?: string;
  primaryButtonLabel?: string;
  primaryButtonDisabled?: boolean;
  secondaryButtonDisabled?: boolean;
  onClickOutside?: () => void;
  onSecondaryClick?: () => void;
  isMore?: boolean;
  onPrimaryClick?: () => void;
  showClose?: boolean;
  onTop?: boolean;
}

const Modal: React.FC<PropsWithChildren<Props>> = ({
  title,
  subtitle,
  secondaryButtonLabel,
  primaryButtonLabel,
  primaryButtonDisabled = false,
  secondaryButtonDisabled = false,
  onClickOutside,
  onSecondaryClick,
  onPrimaryClick,
  isMore,
  children,
  showClose,
  onTop
}) => {
  const userContext = useUserContext();

  const onModalClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const onOutsideClick = () => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: '[Modal] Manually closed modal'
    });

    onClickOutside && onClickOutside();
  }

  return (
    <Overlay onClick={onOutsideClick} $isMore={isMore ?? false} $onTop={onTop}>
      <ModalContainer onClick={onModalClick}>
        {showClose && <ModalClose onClick={onOutsideClick}><CloseIcon/></ModalClose>}
        {title !== undefined ? <Title>{title}</Title> : <></>}
        {subtitle !== undefined ? <Subtitle>{subtitle}</Subtitle> : <></>}
        {children}
        {primaryButtonLabel !== undefined || secondaryButtonLabel !== undefined ? <ButtonGroup>
          {secondaryButtonLabel &&
            <Wrapper>
              <ActionButton outline={true} onClick={onSecondaryClick} disabled={secondaryButtonDisabled}>
                {secondaryButtonLabel}
              </ActionButton>
            </Wrapper>
          }
          {primaryButtonLabel &&
            <Wrapper>
              <ActionButton onClick={onPrimaryClick} disabled={primaryButtonDisabled}>
                {primaryButtonLabel}
              </ActionButton>
            </Wrapper>
          }
        </ButtonGroup> : <></>}

      </ModalContainer>
    </Overlay>
  );
};

const Overlay = styled.div<{ $isMore: boolean, $onTop?: boolean }>`
  top: 0;
  left: 0;
  position: absolute;
  display: flex;
  flex-direction: column;
  background: ${({ $isMore }) => ($isMore ? 'rgb(0,0,0,0)' : 'rgb(0, 0, 0, .8)')};
  align-items: center;
  justify-content: ${({ $isMore }) => ($isMore ? 'end' : 'center')};
  z-index: ${({$onTop}) => $onTop ? '1200' : '200'};
  width: 100%;
  height: 100%;
  color: ${BLACK};
`;

const Title = styled.h4`
  margin: 0;
  font-size: 24px;
  padding: 10px 10px;
  text-align: center;
`;

const Subtitle = styled.div`
  margin: 0;
  font-size: 18px;
  font-weight: 300;
  padding: 10px 10px;
`;

const ButtonGroup = styled.div`
  display: flex;
  margin-top: 11px;
`;

export const Wrapper = styled.div`
  margin: 0 5px;
  width: 100%;
  display: flex;
`;

export default Modal;
